import React, { useState, useEffect } from 'react';
import { Modal, Spin, Alert, Button, Space } from 'antd';
import { FullscreenOutlined, FullscreenExitOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import AnnotationIframe from './AnnotationIframe';

const AnnotationModal = ({ 
    visible, 
    annotationUrl, 
    onClose, 
    title = "PDF Annotation",
    width = "90vw",
    height = "95vh"
}) => {
    const { token, companyId } = useSelector(state => state.auth);
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(true);

    // Reset states when modal opens/closes
    useEffect(() => {
        if (visible) {
            setIsLoading(true);
            setHasError(false);
            setIsFullscreen(true); // open in fullscreen by default
        }
    }, [visible, annotationUrl]);

    const handleIframeLoad = () => {
        setIsLoading(false);
    };

    const handleIframeError = () => {
        setIsLoading(false);
        setHasError(true);
    };

    const handleClose = () => {
        setIsFullscreen(false);
        onClose();
    };

    const toggleFullscreen = () => {
        setIsFullscreen(!isFullscreen);
    };

    const modalStyle = {
        top: isFullscreen ? 0 : 20,
        paddingBottom: isFullscreen ? 0 : 20,
        maxWidth: isFullscreen ? '100vw' : width,
        width: isFullscreen ? '100vw' : width,
        height: isFullscreen ? '100vh' : height,
        margin: isFullscreen ? 0 : 'auto',
        maxHeight: isFullscreen ? '100vh' : '95vh !important'
    };

    const bodyStyle = {
        height: isFullscreen ? '100%' : 'calc(95vh - 110px)',
        padding: isFullscreen ? 0 : 16,
        overflow: 'hidden',
        maxHeight: isFullscreen ? '100%' : 'calc(95vh - 110px) !important'
    };

    return (
        <>
            {/* CSS Overrides for Modal Height */}
            <style>
                {`
                    .annotation-modal-override .ant-modal {
                        max-height: 95vh !important;
                        height: 95vh !important;
                        top: 2.5vh !important;
                    }
                    .annotation-modal-override .ant-modal-body {
                        max-height: calc(95vh - 110px) !important;
                        height: calc(95vh - 110px) !important;
                        overflow: hidden !important;
                        padding: 16px !important;
                    }
                    .annotation-modal-override .ant-modal-content {
                        height: 95vh !important;
                        max-height: 95vh !important;
                        display: flex !important;
                        flex-direction: column !important;
                    }
                    .annotation-modal-override .ant-modal-wrap {
                        display: flex !important;
                        align-items: center !important;
                        justify-content: center !important;
                    }
                    .annotation-modal-override .ant-modal-header {
                        flex-shrink: 0 !important;
                        padding-right: 16px !important;
                        display: flex !important;
                        align-items: center !important;
                        justify-content: space-between !important;
                    }
                    .annotation-modal-override .ant-modal-header .ant-modal-title {
                        flex: 1 !important;
                        margin-right: 16px !important;
                        width: 100% !important;
                    }
                    .annotation-modal-override .ant-modal-header .ant-modal-close {
                        margin-left: 8px !important;
                        flex-shrink: 0 !important;
                        position: absolute !important;
                        right: 16px !important;
                        top: 50% !important;
                        transform: translateY(-50%) !important;
                    }
                    .annotation-modal-override .ant-modal-body {
                        flex: 1 !important;
                        overflow: hidden !important;
                    }
                    @media (max-height: 600px) {
                        .annotation-modal-override .ant-modal {
                            height: 98vh !important;
                            max-height: 98vh !important;
                            top: 1vh !important;
                        }
                        .annotation-modal-override .ant-modal-body {
                            height: calc(98vh - 110px) !important;
                            max-height: calc(98vh - 110px) !important;
                        }
                    }
                    @media (max-width: 768px) {
                        .annotation-modal-override .ant-modal-header {
                            padding: 12px 16px !important;
                        }
                        .annotation-modal-override .ant-modal-header .ant-modal-title {
                            font-size: 14px !important;
                            margin-right: 8px !important;
                        }
                        .annotation-modal-override .ant-modal-header .ant-modal-close {
                            right: 12px !important;
                        }
                    }

                    /* Fullscreen overrides */
                    .annotation-modal-fullscreen .ant-modal-wrap {
                        inset: 0 !important;
                        margin: 0 !important;
                        padding: 0 !important;
                    }
                    .annotation-modal-fullscreen .ant-modal {
                        width: 100vw !important;
                        max-width: 100vw !important;
                        height: 100vh !important;
                        max-height: 100vh !important;
                        top: 0 !important;
                        margin: 0 !important;
                        padding: 0 !important;
                    }
                    .annotation-modal-fullscreen .ant-modal-content {
                        height: 100vh !important;
                        max-height: 100vh !important;
                        border-radius: 0 !important;
                        display: flex !important;
                        flex-direction: column !important;
                    }
                    .annotation-modal-fullscreen .ant-modal-body {
                        flex: 1 !important;
                        height: 100% !important;
                        max-height: 100% !important;
                        padding: 0 !important;
                        overflow: hidden !important;
                    }
                `}
            </style>
            <Modal
                className={`annotation-modal-override ${isFullscreen ? 'annotation-modal-fullscreen' : ''}`}
                title={
                    <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        width: '100%',
                        paddingRight: '40px' // Make space for the close button
                    }}>
                        <span style={{ flex: 1, marginRight: 16 }}>{title}</span>
                        <Button
                            type="text"
                            icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
                            onClick={toggleFullscreen}
                            title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
                            style={{ 
                                marginRight: 8,
                                flexShrink: 0,
                                marginTop: -8 // Aligns with modal close button (X) - adjust if header height changes
                            }}
                        />
                    </div>
                }
                open={visible}
                onCancel={handleClose}
                footer={null}
                width={modalStyle.width}
                style={modalStyle}
                styles={{ body: bodyStyle }}
                destroyOnClose={true}
                maskClosable={false}
                keyboard={true}
                centered={!isFullscreen}
                closable={true}
                getContainer={() => document.body}
            >
            <div style={{ 
                position: 'relative', 
                width: '100%', 
                height: '100%',
                minHeight: isFullscreen ? '100%' : 'calc(95vh - 142px)',
                backgroundColor: '#f5f5f5',
                borderRadius: isFullscreen ? 0 : '8px',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column'
            }}>
                {isLoading && (
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        zIndex: 1000
                    }}>
                        <div style={{ textAlign: 'center' }}>
                            <Spin size="large" />
                            <div style={{ marginTop: 16, color: '#666' }}>
                                Loading annotation system...
                            </div>
                        </div>
                    </div>
                )}
                
                {hasError && (
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000
                    }}>
                        <Alert
                            message="Failed to Load Annotation System"
                            description="The annotation system could not be loaded. Please try again or contact support."
                            type="error"
                            showIcon
                            action={
                                <Button size="small" danger onClick={() => window.location.reload()}>
                                    Retry
                                </Button>
                            }
                        />
                    </div>
                )}

                {annotationUrl && (
                    <AnnotationIframe
                        annotationUrl={annotationUrl}
                        onLoad={handleIframeLoad}
                        onError={handleIframeError}
                        style={{ 
                            width: '100%', 
                            height: '100%',
                            minHeight: isFullscreen ? '100%' : 'calc(95vh - 142px)',
                            flex: 1,
                            display: hasError ? 'none' : 'block',
                            zIndex: 1
                        }}
                    />
                )}
            </div>
            </Modal>
        </>
    );
};

export default AnnotationModal; 